/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent, LoadingButtonComponent } from '../../../../../core/components';
import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { DropdownModel, ErrorResponseModel } from '../../../../../core/models';
import { ExecuteApiService } from '../../../shared/http';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LoadingService, LogService, NotifyService, RbacService } from '../../../../../core/services';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-execute-shell-command',
    templateUrl: './execute.component.html',
    styleUrls: ['./execute.component.scss']
})

export class ExecuteComponent extends BaseComponent implements OnInit {
    /**
     * Get reference to the commandLine input field.
     */
    @ViewChild('commandLine', { static: true })
    public commandLine: ElementRef;

    /**
     * Reference to DOM button for sign in from the loading component.
     */
    @ViewChild(LoadingButtonComponent)
    public executeButton: ElementRef<LoadingButtonComponent>;

    /**
     * To store the command form.
     */
    public commandForm: FormGroup;

    /**
     * Dropdown to contain the list of common commands.
     */
    public dropDownCommands: DropdownModel[];

    /**
     * Store the returned command.
     */
    public returnedCommand: string;

    /**
     * Support request.
     */
    public supportRequest: boolean = false;

    /**
     * Store the selected command string.
     */
    public selectedCommand: string;

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    constructor(
        private executeApiService: ExecuteApiService,
        private formBuilder: FormBuilder,
        private loadingService: LoadingService,
        private logService: LogService,
        private notifyService: NotifyService,
        private rbacService: RbacService
    ) {
        super();
        this.setUpForm();
        this.registerButtons();
    }

    /**
     * Set up the page.
     */
    public ngOnInit(): void {
        this.convertCommandsForDropDown();
    }

    /**
     * Add a host listener for the enter key being pressed; mimic a click on the execute button.
     * @param event
     */
    @HostListener('keyup', ['$event'])
    public onKeyUpHandler(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            this.executeButton['buttonReference'].nativeElement.click();
        }
    }

    /**
     * Populate the command line input with the selected command from the drop-down.
     * @param command
     */
    public dropDownOutput(command: { key: any; value: any; }): void {
        this.selectedCommand = command.key + command.value;
        this.commandForm.get('commandString').setValue(this.selectedCommand);
        this.commandLine.nativeElement.focus();
        this.commandForm.markAsDirty();
    }

    /**
     * Send the input command to the API.
     */
    public sendCommand(): void {
        this.notifyService.muteLocalNotifications();
        this.supportRequest = false;
        const commandString = this.commandForm.value.commandString;
        if (commandString == null) {
            return;
        }
        if (commandString === 'snake') {
            this.returnedCommand = 'support';
            this.supportRequest = true;
            this.commandForm.controls['commandString'].setValue('');
            this.loadingService.loadingButtonEmitter.next(false);
        } else {
            this.executeApiService.executeCommand(commandString, { timeout: 35000 })
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (data: any): void => {
                        this.returnedCommand = this.colorCodeData(data);
                        this.commandForm.controls['commandString'].setValue('');
                    },
                    error: (error: ErrorResponseModel): void => {
                        // Gracefully handle certain error conditions with a notification.
                        if (/timed out|host is busy/.test(error.message)) {
                            this.returnedCommand = '';
                            this.notifyService.unmuteLocalNotifications();
                            this.notifyService.error(error.message);
                        } else {
                            this.returnedCommand = error.message;
                        }
                        this.commandForm.controls['commandString'].setValue('');
                        this.logService.error('ExecuteComponent: sendCommand', error.message);
                    }
                })
                .add((): void => {
                    this.loadingService.loadingButtonEmitter.next(false);
                    this.notifyService.unmuteLocalNotifications();
                });
        }
    }

    /**
     * Set up form group.
     * @private
     */
    private setUpForm(): void {
        this.commandForm = this.formBuilder.group({
            commandString: [null, Validators.required]
        });
    }

    /**
     * Colour code response of type data.
     * @param response
     * @private
     */
    private colorCodeData(response: any): string {
        const newLines = [];
        for (const line of response) {
            if (/^(\d+):/.test(line)) {
                newLines.push(`<span class="number">${line}</span>`);
            } else if (/^[A-Z]/.test(line)) {
                newLines.push(`<span class="key">${line}</span>`);
            } else {
                newLines.push(line);
            }
        }

        return newLines.join('\n');
    }

    /**
     * Convert commands list array for use with the dropdown.
     * @private
     */
    private convertCommandsForDropDown(): void {
        this.dropDownCommands = [
            <DropdownModel>{
                key: 'ping -c 3 ',
                value: 'google.com'
            },
            {
                key: 'top -bn1',
                value: ''
            },
            {
                key: 'arping -I ens33 -c 3 ',
                value: ' 127.0.0.1'
            },
            {
                key: 'ip a',
                value: ''
            },
            {
                key: 'ps uxw',
                value: ''
            },
            {
                key: 'ps ax',
                value: ''
            },
            {
                key: 'nmap -sT ',
                value: '127.0.0.1'
            },
            {
                key: 'dig ',
                value: ''
            },
            {
                key: 'host -t mx ',
                value: 'google.com'
            },
            {
                key: 'host -t ns ',
                value: 'google.com'
            }
        ];
    }

    /**
     * Register the page's buttons for RBAC.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'exec-shell-comm-execute': [{ path: '/system/execute', methods: ['PUT'] }]
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);

        if (!this.permissionsLookup['exec-shell-comm-execute']) {
            this.commandForm.disable();
        }
    }
}
